import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'other';
  timestamp: string;
  read: boolean;
  replyTo?: {
    id: string;
    text: string;
    sender: 'user' | 'other';
  };
  reactions?: {
    emoji: string;
    count: number;
    users: string[];
  }[];
  attachments?: {
    type: 'image' | 'file' | 'product' | 'order';
    url: string;
    title?: string;
    price?: string;
    status?: 'pending' | 'delivered' | 'revision' | 'completed';
  }[];
}

interface Conversation {
  id: string;
  user: {
    id: string;
    name: string;
    avatar: string;
    status?: 'online' | 'offline' | 'away';
    role?: 'seller' | 'buyer';
    rating?: number;
  };
  lastMessage: {
    text: string;
    timestamp: string;
    read: boolean;
  };
  messages: Message[];
  orderId?: string;
  orderStatus?: 'active' | 'pending' | 'completed' | 'cancelled';
  productType?: 'script' | 'animation' | 'build' | 'map' | 'gamemode' | 'schema';
}

// Animation variants
const pageVariants = {
  initial: { opacity: 0 },
  animate: {
    opacity: 1,
    transition: { duration: 0.4 }
  },
  exit: { opacity: 0 }
};

const listVariants = {
  initial: { opacity: 0 },
  animate: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05
    }
  }
};

const itemVariants = {
  initial: { opacity: 0, y: 10 },
  animate: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.3 }
  }
};

const messageVariants = {
  initial: { opacity: 0, y: 10, scale: 0.98 },
  animate: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.2, ease: "easeOut" }
  }
};

// New typing indicator animation
const typingVariants = {
  initial: { opacity: 0, y: 10 },
  animate: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.3 }
  }
};

const pulseVariants = {
  animate: {
    scale: [1, 1.1, 1],
    opacity: [0.7, 1, 0.7],
    transition: {
      duration: 1.2,
      repeat: Infinity,
      repeatType: "loop" as const
    }
  }
};

const MessagesPage: React.FC = () => {
  const [isLoaded, setIsLoaded] = useState(false);

  // Add missing state variables
  const [conversations, setConversations] = useState<Conversation[]>([
    {
      id: '1',
      user: {
        id: 'user1',
        name: 'Alex Johnson',
        avatar: 'https://randomuser.me/api/portraits/men/32.jpg',
        status: 'online'
      },
      lastMessage: {
        text: 'I\'ve sent you the updated files with the requested changes.',
        timestamp: '10:42 AM',
        read: false
      },
      messages: [
        {
          id: 'm1',
          text: 'Hi there! I\'m interested in your React component library.',
          sender: 'other',
          timestamp: 'Yesterday, 9:15 AM',
          read: true
        },
        {
          id: 'm2',
          text: 'Hello! Thanks for reaching out. What specific features are you looking for?',
          sender: 'user',
          timestamp: 'Yesterday, 9:20 AM',
          read: true
        },
        {
          id: 'm3',
          text: 'I need a comprehensive UI kit with dark mode support and accessibility features.',
          sender: 'other',
          timestamp: 'Yesterday, 9:25 AM',
          read: true
        },
        {
          id: 'm4',
          text: 'Perfect, I can customize my package to include those features. Would you like me to prepare a demo with those specifications?',
          sender: 'user',
          timestamp: 'Yesterday, 9:45 AM',
          read: true
        },
        {
          id: 'm5',
          text: 'That would be great! Could you also include some examples of form validation?',
          sender: 'other',
          timestamp: 'Yesterday, 10:15 AM',
          read: true
        },
        {
          id: 'm6',
          text: 'I\'ve sent you the updated files with the requested changes.',
          sender: 'other',
          timestamp: '10:42 AM',
          read: false
        }
      ]
    },
    // More conversations as needed
  ]);

  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(conversations[0]);
  const [newMessage, setNewMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showOptionsMenu, setShowOptionsMenu] = useState(false);
  const [showOrderActions, setShowOrderActions] = useState(false);
  const [showReactionPicker, setShowReactionPicker] = useState(false);
  const [currentReactingMessageId, setCurrentReactingMessageId] = useState<string | null>(null);
  const [replyToMessage, setReplyToMessage] = useState<Message | null>(null);
  const [isTyping, setIsTyping] = useState(false);

  // Report-related state
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportDetails, setReportDetails] = useState('');
  const [reportMessageId, setReportMessageId] = useState<string | null>(null);
  const [reportMessage, setReportMessage] = useState<Message | null>(null);
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [reportContextQuestions, setReportContextQuestions] = useState<string[]>([]);

  // Simulate typing indicator
  useEffect(() => {
    if (selectedConversation) {
      const randomTimeout = Math.random() * 10000 + 5000; // Random time between 5-15 seconds
      const interval = setInterval(() => {
        setIsTyping(true);
        setTimeout(() => setIsTyping(false), 3000);
      }, randomTimeout);

      return () => clearInterval(interval);
    }
  }, [selectedConversation?.id]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoaded(true);
    }, 300);

    return () => clearTimeout(timer);
  }, []);

  // Add missing handler functions
  const handleSelectConversation = (conversation: Conversation) => {
    // Mark all messages as read when selecting a conversation
    const updatedConversations = conversations.map(conv => {
      if (conv.id === conversation.id) {
        const updatedMessages = conv.messages.map(msg => ({
          ...msg,
          read: true
        }));

        return {
          ...conv,
          messages: updatedMessages,
          lastMessage: {
            ...conv.lastMessage,
            read: true
          }
        };
      }
      return conv;
    });

    setConversations(updatedConversations);
    setSelectedConversation(conversation);
  };

  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();

    if (!newMessage.trim() || !selectedConversation) return;

    const updatedConversations = conversations.map(conv => {
      if (conv.id === selectedConversation.id) {
        const newMessageObj: Message = {
          id: `new-${Date.now()}`,
          text: newMessage,
          sender: 'user' as const,
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          read: true
        };

        // Add reply info if replying to a message
        if (replyToMessage) {
          newMessageObj.replyTo = {
            id: replyToMessage.id,
            text: replyToMessage.text,
            sender: replyToMessage.sender
          };
        }

        return {
          ...conv,
          messages: [...conv.messages, newMessageObj],
          lastMessage: {
            text: newMessage,
            timestamp: 'Just now',
            read: true
          }
        };
      }
      return conv;
    });

    setConversations(updatedConversations);
    setSelectedConversation(updatedConversations.find(conv => conv.id === selectedConversation.id) || null);
    setNewMessage('');
    setReplyToMessage(null);
  };

  const filteredConversations = conversations.filter(conv =>
    conv.user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    conv.lastMessage.text.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'online': return 'bg-green-500';
      case 'away': return 'bg-yellow-500';
      default: return 'bg-gray-500';
    }
  };

  const getUnreadCount = (conversation: Conversation) => {
    return conversation.messages.filter(msg => !msg.read && msg.sender === 'other').length;
  };

  const handleAttachFile = () => {
    // In a real app, this would open a file picker
    alert("File attachment feature would open here");
  };

  const handleReportReasonChange = (reason: string) => {
    setReportReason(reason);

    // Set contextual questions based on the selected reason
    switch(reason) {
      case 'Inappropriate content':
        setReportContextQuestions([
          'What type of inappropriate content does this message contain?',
          'Does this content violate specific community guidelines?',
          'Have you noticed similar messages from this user?'
        ]);
        break;
      case 'Spam':
        setReportContextQuestions([
          'Is this message unsolicited advertising?',
          'Have you received multiple similar messages?',
          'Is this message part of a mass-messaging campaign?'
        ]);
        break;
      case 'Scam/Fraud':
        setReportContextQuestions([
          'What makes you believe this is a scam or fraudulent message?',
          'Has the user requested money or sensitive information?',
          'Have they made promises that seem unrealistic?'
        ]);
        break;
      case 'Harassment':
        setReportContextQuestions([
          'Is this part of a pattern of unwanted communication?',
          'Does this message contain threats or intimidation?',
          'Have you asked the user to stop contacting you?'
        ]);
        break;
      case 'Copyright violation':
        setReportContextQuestions([
          'What copyrighted material is being used without permission?',
          'Are you the copyright owner?',
          'Do you have evidence of ownership you can provide?'
        ]);
        break;
      case 'Other':
        setReportContextQuestions([
          'Please describe the issue in detail',
          'Why do you believe this message should be reported?'
        ]);
        break;
      default:
        setReportContextQuestions([]);
    }
  };

  const handleReportMessage = (messageId: string) => {
    // Find the message to report
    if (!selectedConversation) return;

    const messageToReport = selectedConversation.messages.find(msg => msg.id === messageId);
    if (!messageToReport) return;

    // Set state
    setReportMessageId(messageId);
    setReportMessage(messageToReport);
    setReportReason('');
    setReportDetails('');
    setReportContextQuestions([]);
    setShowReportModal(true);
  };

  const submitReport = () => {
    if (!reportReason.trim() || !reportMessageId) return;

    setReportSubmitting(true);

    // Simulate API call
    setTimeout(() => {
    // In a real app, you would send this to your API
      console.log(`Report submitted for message ID: ${reportMessageId}. Reason: ${reportReason}`);
      console.log(`Additional details: ${reportDetails}`);

      // Reset state
      setReportSubmitting(false);
    setShowReportModal(false);
    setReportReason('');
      setReportDetails('');
    setReportMessageId(null);
      setReportMessage(null);
      setReportContextQuestions([]);

      // Show success notification
      alert('Thank you for your report. Our moderation team will review it shortly.');
    }, 1500);
  };

  const handleCompleteOrder = () => {
    if (!selectedConversation?.orderId) return;
    alert(`Order ${selectedConversation.orderId} marked as completed!`);
    // In a real app, update the order status via API
  };

  const handleRequestRevision = () => {
    if (!selectedConversation?.orderId) return;
    alert(`Revision requested for order ${selectedConversation.orderId}`);
  };

  const handleReactionClick = (messageId: string) => {
    setCurrentReactingMessageId(messageId);
    setShowReactionPicker(true);
  };

  const addReaction = (messageId: string, emoji: string) => {
    if (!selectedConversation) return;

    setConversations(prevConversations =>
      prevConversations.map(conv => {
        if (conv.id === selectedConversation.id) {
          const updatedMessages = conv.messages.map(msg => {
            if (msg.id === messageId) {
              // Check if this reaction already exists
              const existingReactions = msg.reactions || [];
              const existingReactionIndex = existingReactions.findIndex(r => r.emoji === emoji);

              let updatedReactions;
              if (existingReactionIndex >= 0) {
                // Update existing reaction
                updatedReactions = [...existingReactions];

                // If user already reacted with this emoji, remove it
                if (updatedReactions[existingReactionIndex].users.includes('user1')) {
                  updatedReactions[existingReactionIndex] = {
                    ...updatedReactions[existingReactionIndex],
                    count: updatedReactions[existingReactionIndex].count - 1,
                    users: updatedReactions[existingReactionIndex].users.filter(u => u !== 'user1')
                  };

                  // Remove reaction if count is zero
                  if (updatedReactions[existingReactionIndex].count === 0) {
                    updatedReactions = updatedReactions.filter(r => r.count > 0);
                  }
                } else {
                  // Add user to existing reaction
                  updatedReactions[existingReactionIndex] = {
                    ...updatedReactions[existingReactionIndex],
                    count: updatedReactions[existingReactionIndex].count + 1,
                    users: [...updatedReactions[existingReactionIndex].users, 'user1']
                  };
                }
              } else {
                // Add new reaction
                updatedReactions = [
                  ...existingReactions,
                  { emoji, count: 1, users: ['user1'] }
                ];
              }

              return {
                ...msg,
                reactions: updatedReactions
              };
            }
            return msg;
          });

          return {
            ...conv,
            messages: updatedMessages
          };
        }
        return conv;
      })
    );

    setShowReactionPicker(false);
    setCurrentReactingMessageId(null);
  };

  const handleReplyToMessage = (message: Message) => {
    setReplyToMessage(message);
  };

  const cancelReply = () => {
    setReplyToMessage(null);
  };

  return (
    <motion.div
      initial="initial"
      animate={isLoaded ? "animate" : "initial"}
      exit="exit"
      variants={pageVariants}
      className="min-h-screen relative py-6"
      style={{
        background: 'linear-gradient(to bottom, #141019, #1A1625)',
      }}
    >
      <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-[0.03]"></div>
      <div className="container mx-auto px-4 relative z-10 max-w-screen-xl">
        <div className="flex items-center justify-between mb-6">
          <motion.h1
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="text-2xl font-bold"
          >
            Messages
          </motion.h1>
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
            className="flex gap-2"
          >
            <button
              className="p-2 rounded-full bg-[#2A2633] hover:bg-[#3A3643] transition-colors"
              onClick={() => alert("New message feature would open here")}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 5V19M5 12H19" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <button
              className="p-2 rounded-full bg-[#2A2633] hover:bg-[#3A3643] transition-colors"
              onClick={() => setShowOptionsMenu(!showOptionsMenu)}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 13C12.5523 13 13 12.5523 13 12C13 11.4477 12.5523 11 12 11C11.4477 11 11 11.4477 11 12C11 12.5523 11.4477 13 12 13Z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M19 13C19.5523 13 20 12.5523 20 12C20 11.4477 19.5523 11 19 11C18.4477 11 18 11.4477 18 12C18 12.5523 18.4477 13 19 13Z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M5 13C5.55228 13 6 12.5523 6 12C6 11.4477 5.55228 11 5 11C4.44772 11 4 11.4477 4 12C4 12.5523 4.44772 13 5 13Z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </motion.div>

          {/* Options menu dropdown */}
          {showOptionsMenu && (
            <div className="absolute right-4 top-12 bg-[#2A2633] rounded-lg shadow-lg border border-[#3A3643] p-2 z-20">
              <ul>
                <li className="px-4 py-2 hover:bg-[#3A3643] rounded cursor-pointer">Settings</li>
                <li className="px-4 py-2 hover:bg-[#3A3643] rounded cursor-pointer">Mark all as read</li>
                <li className="px-4 py-2 hover:bg-[#3A3643] rounded cursor-pointer">My Orders</li>
                <li className="px-4 py-2 hover:bg-[#3A3643] rounded cursor-pointer">Archived Chats</li>
              </ul>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 min-h-[calc(100vh-180px)]">
          {/* Conversation list */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-[#1F1A27] rounded-xl overflow-hidden md:col-span-1 flex flex-col"
          >
            <div className="p-4 border-b border-[#2A2633]">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search messages..."
                  className="w-full bg-[#2A2633] text-white border-none rounded-lg py-2 pl-10 pr-4 focus:ring-1 focus:ring-[#F670C7] focus:outline-none placeholder-gray-500"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <svg
                  className="absolute left-3 top-2.5 w-5 h-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
                </svg>
              </div>
            </div>

            {/* Conversations */}
            <div className="overflow-y-auto flex-grow custom-scrollbar">
              <AnimatePresence>
                <motion.div variants={listVariants} initial="initial" animate="animate">
                  {filteredConversations.length > 0 ? (
                    filteredConversations.map((conversation) => {
                      const unreadCount = getUnreadCount(conversation);
                      return (
                        <motion.div
                          key={conversation.id}
                          variants={itemVariants}
                          className={`p-4 border-b border-[#2A2633] cursor-pointer transition-colors ${
                            selectedConversation?.id === conversation.id
                              ? 'bg-[#2A2633]'
                              : 'hover:bg-[#2A2633]/50'
                          }`}
                          onClick={() => handleSelectConversation(conversation)}
                        >
                          <div className="flex items-center">
                            <div className="relative flex-shrink-0">
                              <img
                                src={conversation.user.avatar}
                                alt={conversation.user.name}
                                className="w-12 h-12 rounded-full object-cover border-2 border-[#1F1A27]"
                              />
                              <div
                                className={`absolute bottom-0 right-0 w-3 h-3 ${getStatusColor(conversation.user.status)} rounded-full border-2 border-[#1F1A27]`}
                              ></div>
                            </div>
                            <div className="ml-3 flex-1">
                              <div className="flex justify-between">
                                <h3 className="font-semibold text-base">{conversation.user.name}</h3>
                                <span className="text-xs text-gray-400">{conversation.lastMessage.timestamp}</span>
                              </div>
                              <div className="flex justify-between items-center">
                                <p className={`text-sm truncate max-w-[180px] ${!conversation.lastMessage.read && conversation.id !== selectedConversation?.id ? 'text-white font-medium' : 'text-gray-400'}`}>
                                  {conversation.lastMessage.text}
                                </p>
                                {unreadCount > 0 && conversation.id !== selectedConversation?.id && (
                                  <span className="bg-[#F670C7] text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                                    {unreadCount}
                                  </span>
                                )}
                              </div>
                              {conversation.orderStatus && (
                                <div className="flex items-center mt-1">
                                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                                    conversation.orderStatus === 'active' ? 'bg-green-900/30 text-green-400' :
                                    conversation.orderStatus === 'pending' ? 'bg-yellow-900/30 text-yellow-400' :
                                    conversation.orderStatus === 'completed' ? 'bg-blue-900/30 text-blue-400' :
                                    'bg-red-900/30 text-red-400'
                                  }`}>
                                    {conversation.productType && `${conversation.productType} • `}
                                    {conversation.orderStatus}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        </motion.div>
                      );
                    })
                  ) : (
                    <div className="p-8 text-center text-gray-400">
                      <svg className="w-12 h-12 mx-auto mb-3 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16l2.879-2.879m0 0a3 3 0 104.243-4.242 3 3 0 00-4.243 4.242zM21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                      </svg>
                      <p>No conversations found</p>
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>
            </div>
          </motion.div>

          {/* Chat area */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-[#1F1A27] rounded-xl overflow-hidden md:col-span-2 flex flex-col"
          >
            {selectedConversation ? (
              <>
                {/* Chat header */}
                <div className="p-4 border-b border-[#2A2633] flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="relative">
                      <img
                        src={selectedConversation.user.avatar}
                        alt={selectedConversation.user.name}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                      <div
                        className={`absolute bottom-0 right-0 w-3 h-3 ${getStatusColor(selectedConversation.user.status)} rounded-full border-2 border-[#1F1A27]`}
                      ></div>
                    </div>
                    <div className="ml-3">
                      <h3 className="font-semibold text-base">
                        {selectedConversation.user.name}
                        {selectedConversation.user.role && (
                          <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${
                            selectedConversation.user.role === 'seller' ? 'bg-purple-900/30 text-purple-400' : 'bg-blue-900/30 text-blue-400'
                          }`}>
                            {selectedConversation.user.role}
                          </span>
                        )}
                      </h3>
                      <p className="text-xs text-gray-400 flex items-center">
                        {selectedConversation.user.status === 'online' ? 'Online' :
                          selectedConversation.user.status === 'away' ? 'Away' : 'Offline'}

                        {selectedConversation.user.rating && (
                          <span className="ml-2 flex items-center">
                            <svg className="w-3 h-3 text-yellow-500" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
                            </svg>
                            <span className="ml-1 text-xs">{selectedConversation.user.rating}</span>
                          </span>
                        )}
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    {selectedConversation.orderStatus && (
                      <button
                        className="p-2 rounded-full bg-[#2A2633] hover:bg-[#3A3643] transition-colors"
                        onClick={() => setShowOrderActions(!showOrderActions)}
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" xmlns="http://www.w3.org/2000/svg">
                          <path d="M22 12C22 17.5 17.5 22 12 22C6.5 22 2 17.5 2 12C2 6.5 6.5 2 12 2C17.5 2 22 6.5 22 12Z" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M15.9 10.8L12 14.7L8.10001 10.8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                    )}
                    <button
                      className="p-2 rounded-full bg-[#2A2633] hover:bg-[#3A3643] transition-colors"
                      onClick={() => alert("Add action feature would open here")}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 5V19M5 12H19" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  </div>

                  {/* Order actions dropdown */}
                  {showOrderActions && selectedConversation.orderStatus && (
                    <div className="absolute right-4 top-16 bg-[#2A2633] rounded-lg shadow-lg border border-[#3A3643] p-2 z-20 w-48">
                      <ul>
                        <li className="px-4 py-2 hover:bg-[#3A3643] rounded cursor-pointer flex items-center" onClick={() => alert("Deliver Order feature would open here")}>
                          <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" xmlns="http://www.w3.org/2000/svg">
                            <path d="M22 12C22 17.5 17.5 22 12 22C6.5 22 2 17.5 2 12C2 6.5 6.5 2 12 2C17.5 2 22 6.5 22 12Z" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M8 12L11 15L16 10" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          Deliver Order
                        </li>
                        <li className="px-4 py-2 hover:bg-[#3A3643] rounded cursor-pointer flex items-center" onClick={handleRequestRevision}>
                          <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" xmlns="http://www.w3.org/2000/svg">
                            <path d="M22 12C22 17.5 17.5 22 12 22C6.5 22 2 17.5 2 12C2 6.5 6.5 2 12 2C17.5 2 22 6.5 22 12Z" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M8.5 12H14.5" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M12.5 15L15.5 12L12.5 9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          Request Revision
                        </li>
                        <li className="px-4 py-2 hover:bg-[#3A3643] rounded cursor-pointer flex items-center" onClick={handleCompleteOrder}>
                          <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" xmlns="http://www.w3.org/2000/svg">
                            <path d="M22 12C22 17.5 17.5 22 12 22C6.5 22 2 17.5 2 12C2 6.5 6.5 2 12 2C17.5 2 22 6.5 22 12Z" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M7.75 12.75L10 15L16 9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          Complete Order
                        </li>
                      </ul>
                    </div>
                  )}
                </div>

                {/* Order info banner (if applicable) */}
                {selectedConversation.orderId && (
                  <div className={`px-4 py-2 ${
                    selectedConversation.orderStatus === 'active' ? 'bg-green-900/20' :
                    selectedConversation.orderStatus === 'pending' ? 'bg-yellow-900/20' :
                    selectedConversation.orderStatus === 'completed' ? 'bg-blue-900/20' :
                    'bg-red-900/20'
                  }`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <span className="text-xs font-medium">Order #{selectedConversation.orderId}</span>
                        <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${
                          selectedConversation.orderStatus === 'active' ? 'bg-green-900/30 text-green-400' :
                          selectedConversation.orderStatus === 'pending' ? 'bg-yellow-900/30 text-yellow-400' :
                          selectedConversation.orderStatus === 'completed' ? 'bg-blue-900/30 text-blue-400' :
                          'bg-red-900/30 text-red-400'
                        }`}>
                          {selectedConversation.orderStatus}
                        </span>
                      </div>
                      <button className="text-xs text-purple-400 hover:text-purple-300">
                        View Details
                      </button>
                    </div>
                  </div>
                )}

                {/* Chat messages */}
                <div
                  className="flex-grow overflow-y-auto py-6 px-4 custom-scrollbar relative"
                  style={{
                    paddingBottom: '2rem',
                    backgroundImage: 'radial-gradient(circle at center, rgba(125, 86, 217, 0.03) 0%, transparent 70%)',
                    backgroundSize: '100% 100%',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat'
                  }}
                >
                  <div className="flex flex-col space-y-6">
                    <AnimatePresence>
                      {selectedConversation.messages.map((message) => (
                        <motion.div
                          key={message.id}
                          variants={messageVariants}
                          initial="initial"
                          animate="animate"
                          className={`flex w-full ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                        >
                          {message.sender === 'other' && (
                            <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0 mr-2 mt-1">
                              <img
                                src={selectedConversation.user.avatar}
                                alt={selectedConversation.user.name}
                                className="w-full h-full object-cover"
                              />
                            </div>
                          )}

                          <div className="group relative mr-8 mb-4">
                            {/* Show reply reference if it's a reply */}
                            {message.replyTo && (
                              <div
                                className={`mb-0.5 ${
                                  message.sender === 'user'
                                    ? 'ml-auto max-w-[270px] md:max-w-md'
                                    : 'max-w-[270px] md:max-w-md'
                                }`}
                              >
                                <div
                                  className={`rounded-t-lg px-3 py-2 ${
                                    message.sender === 'user'
                                      ? 'bg-[#7F56D9] bg-opacity-90 backdrop-blur-sm'
                                      : 'bg-[#38343F] bg-opacity-90 backdrop-blur-sm'
                                  }`}
                                >
                                  <div className="flex items-center relative">
                                    <div className="w-0.5 h-full absolute left-0 top-0 bottom-0 bg-gradient-to-b from-white/50 to-white/20"></div>
                                    <p className="text-xs text-white/80 font-medium pl-3">
                                      Replying to {message.replyTo.sender === 'user' ? 'You' : selectedConversation?.user.name.split(' ')[0]}
                                    </p>
                                  </div>
                                  <p className="text-xs text-white/90 pl-3 mt-1 line-clamp-1">
                                    {message.replyTo.text}
                                  </p>
                                </div>
                              </div>
                            )}

                            <div
                              className={`${
                                message.sender === 'user'
                                  ? 'bg-gradient-to-br from-[#7F56D9] to-[#9D4EDD] text-white shadow-sm shadow-purple-500/10 max-w-[270px] md:max-w-md'
                                  : 'bg-[#2A2633] text-white shadow-sm shadow-black/5 max-w-[270px] md:max-w-md border border-[#3A3643]/30'
                              } ${message.replyTo ? 'rounded-t-none rounded-b-2xl rounded-r-2xl' : 'rounded-2xl'} px-4 py-3 backdrop-blur-sm`}
                            >
                              <p className="text-sm sm:text-base">{message.text}</p>

                              {/* Message attachments like product/file previews */}
                              {message.attachments && message.attachments.length > 0 && (
                                <div className="mt-3 space-y-3">
                                  {message.attachments.map((attachment, idx) => (
                                    <div
                                      key={idx}
                                      className={`p-3 rounded-lg ${
                                        message.sender === 'user' ? 'bg-[#6D4BC0]/70 backdrop-blur-sm' : 'bg-[#3A3643]/70 backdrop-blur-sm'
                                      }`}
                                    >
                                      {attachment.type === 'product' && (
                                        <div className="flex items-center">
                                          <div className="w-12 h-12 bg-[#2A2633] rounded-md overflow-hidden flex-shrink-0">
                                            <img src={attachment.url} alt={attachment.title} className="w-full h-full object-cover" />
                                          </div>
                                          <div className="ml-3 flex-1">
                                            <p className="text-sm font-medium">{attachment.title}</p>
                                            {attachment.price && (
                                              <p className="text-xs text-purple-300">{attachment.price}</p>
                                            )}
                                          </div>
                                        </div>
                                      )}

                                      {attachment.type === 'order' && (
                                        <div>
                                          <div className="flex justify-between items-center mb-2">
                                            <span className="text-xs font-medium">Order Delivery</span>
                                            {attachment.status && (
                                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                                attachment.status === 'delivered' ? 'bg-green-900/30 text-green-400' :
                                                attachment.status === 'revision' ? 'bg-yellow-900/30 text-yellow-400' :
                                                'bg-blue-900/30 text-blue-400'
                                              }`}>
                                                {attachment.status}
                                              </span>
                                            )}
                                          </div>
                                          <div className="flex items-center">
                                            <div className="w-8 h-8 bg-[#2A2633] rounded-md overflow-hidden flex-shrink-0 flex items-center justify-center">
                                              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M9 17L15 11L9 5" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                              </svg>
                                            </div>
                                            <div className="ml-3 flex-1">
                                              <p className="text-sm">{attachment.title}</p>
                                            </div>
                                          </div>
                                          <div className="mt-2 flex justify-end space-x-2">
                                            <button className="text-xs bg-[#2A2633] hover:bg-[#3A3643] px-2 py-1 rounded">
                                              Download
                                            </button>
                                            <button className="text-xs bg-[#2A2633] hover:bg-[#3A3643] px-2 py-1 rounded">
                                              Preview
                                            </button>
                                          </div>
                                        </div>
                                      )}

                                      {attachment.type === 'image' && (
                                        <div className="rounded-md overflow-hidden">
                                          <img src={attachment.url} alt="Attached" className="w-full h-auto max-h-48 object-cover" />
                                        </div>
                                      )}

                                      {attachment.type === 'file' && (
                                        <div className="flex items-center">
                                          <div className="w-8 h-8 bg-[#2A2633] rounded-md overflow-hidden flex-shrink-0 flex items-center justify-center">
                                            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" xmlns="http://www.w3.org/2000/svg">
                                              <path d="M13 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V9L13 2Z" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                              <path d="M13 2V9H20" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                          </div>
                                          <div className="ml-3 flex-1">
                                            <p className="text-sm">{attachment.title || 'File'}</p>
                                          </div>
                                          <button className="text-xs bg-[#2A2633] hover:bg-[#3A3643] px-2 py-1 rounded ml-2">
                                            Download
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}

                              <div className={`text-xs mt-1 flex items-center ${message.sender === 'user' ? 'text-white/70 justify-end' : 'text-gray-400'}`}>
                                {message.timestamp}
                                {message.sender === 'user' && (
                                  <svg
                                    className={`ml-1 w-4 h-4 ${message.read ? 'text-blue-400' : 'text-white/70'}`}
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                  >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                  </svg>
                                )}
                              </div>
                            </div>

                            {/* Message actions toolbar (visible on hover) */}
                            <div
                              className={`absolute ${message.sender === 'user' ? 'left-0 -translate-x-full' : 'right-0 translate-x-full'}
                              top-0 scale-0 w-0 overflow-hidden group-hover:scale-100 group-hover:w-auto opacity-0 group-hover:opacity-100
                              transition-all duration-200 flex flex-row items-center space-x-1 z-10 px-1 py-1
                              bg-[#1F1A27]/80 backdrop-blur-sm rounded-md origin-${message.sender === 'user' ? 'right' : 'left'}`}
                            >
                              <button
                                className="p-1.5 rounded-full bg-[#2A2633] hover:bg-[#3A3643] text-white"
                                onClick={() => handleReactionClick(message.id)}
                              >
                                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                  <path d="M8 14C8 14 9.5 16 12 16C14.5 16 16 14 16 14" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                  <path d="M9 9H9.01" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                  <path d="M15 9H15.01" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              </button>
                              <button
                                className="p-1.5 rounded-full bg-[#2A2633] hover:bg-[#3A3643] text-white"
                                onClick={() => handleReplyToMessage(message)}
                              >
                                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" xmlns="http://www.w3.org/2000/svg">
                                  <path d="M9 8L5 12L9 16" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                  <path d="M5 12H16C18.2091 12 20 10.2091 20 8C20 5.79086 18.2091 4 16 4" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              </button>

                              {message.sender === 'other' && (
                                <button
                                  className="p-1.5 rounded-full bg-[#2A2633] hover:bg-[#3A3643] text-red-400"
                                  onClick={() => handleReportMessage(message.id)}
                                >
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                  </svg>
                                </button>
                              )}
                            </div>

                            {/* Display message reactions */}
                            {message.reactions && message.reactions.length > 0 && (
                              <div
                                className={`absolute ${message.sender === 'user' ? 'left-0 -translate-x-[calc(100%+4px)]' : 'right-0 translate-x-[calc(100%+4px)]'}
                                bottom-0 scale-75 group-hover:scale-100 opacity-70 group-hover:opacity-100
                                transition-all duration-200 flex flex-row bg-[#1F1A27] rounded-lg px-1 py-1 border border-[#3A3643] z-20`}
                              >
                                {message.reactions.map((reaction, index) => (
                                  <button
                                    key={index}
                                    className={`text-sm p-1 mx-0.5 rounded-md w-8 h-8 flex items-center justify-center ${reaction.users.includes('user1') ? 'bg-[#3A3643]' : ''}`}
                                    onClick={() => addReaction(message.id, reaction.emoji)}
                                  >
                                    <div>
                                      <span>{reaction.emoji}</span>
                                      {reaction.count > 1 && <span className="ml-0.5 text-xxs align-top">{reaction.count}</span>}
                                    </div>
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>

                          {message.sender === 'user' && (
                            <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0 ml-2 mt-1">
                              <div className="w-full h-full bg-gradient-to-br from-pink-400 to-purple-600 flex items-center justify-center text-white font-bold">
                                {/* Show user initial */}
                                U
                              </div>
                            </div>
                          )}
                        </motion.div>
                      ))}
                    </AnimatePresence>

                    {/* Typing indicator */}
                    {isTyping && (
                      <motion.div
                        variants={typingVariants}
                        initial="initial"
                        animate="animate"
                        className="flex items-center justify-start space-x-2 mt-2"
                      >
                        <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0 mr-2">
                          <img
                            src={selectedConversation.user.avatar}
                            alt={selectedConversation.user.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="bg-[#2A2633] p-3 px-4 rounded-xl flex items-center space-x-1">
                          <motion.div variants={pulseVariants} animate="animate" className="w-2 h-2 bg-gray-400 rounded-full" />
                          <motion.div variants={pulseVariants} animate="animate" transition={{delay: 0.2}} className="w-2 h-2 bg-gray-400 rounded-full" />
                          <motion.div variants={pulseVariants} animate="animate" transition={{delay: 0.4}} className="w-2 h-2 bg-gray-400 rounded-full" />
                        </div>
                      </motion.div>
                    )}
                  </div>
                </div>

                {/* Message input with reply preview */}
                <form onSubmit={handleSendMessage} className="p-4 border-t border-[#2A2633]">
                  {replyToMessage && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      className="mb-2 px-3 py-2 bg-[#2A2633] rounded-lg flex justify-between items-center"
                    >
                      <div className="flex items-center text-sm text-gray-400">
                        <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" xmlns="http://www.w3.org/2000/svg">
                          <path d="M9 8L5 12L9 16" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M5 12H16C18.2091 12 20 10.2091 20 8C20 5.79086 18.2091 4 16 4" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <span className="truncate">
                          Replying to: {replyToMessage.text.length > 30 ? replyToMessage.text.substring(0, 30) + '...' : replyToMessage.text}
                        </span>
                      </div>
                      <button
                        type="button"
                        className="text-gray-500 hover:text-gray-300 transition-colors"
                        onClick={cancelReply}
                      >
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" xmlns="http://www.w3.org/2000/svg">
                          <path d="M18 6L6 18" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M6 6L18 18" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                    </motion.div>
                  )}

                  <div className="flex items-center relative">
                    <div className="flex items-center">
                      <button
                        type="button"
                        className="p-2 rounded-full hover:bg-[#2A2633] transition-colors mr-2 text-gray-400 hover:text-white"
                        onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                      >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" className="transition-colors" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M8 14C8 14 9.5 16 12 16C14.5 16 16 14 16 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M9 9H9.01" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M15 9H15.01" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                      <button
                        type="button"
                        className="p-2 rounded-full hover:bg-[#2A2633] transition-colors mr-2 text-gray-400 hover:text-white"
                        onClick={handleAttachFile}
                      >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="transition-colors" xmlns="http://www.w3.org/2000/svg">
                          <path d="M21 13V11C21 7.13 17.87 4 14 4H10C6.13 4 3 7.13 3 11V13" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M12 15L16 19L20 15" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M16 18V9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                    </div>

                    <input
                      type="text"
                      className="w-full bg-[#2A2633] text-white border-none rounded-lg py-3 px-4 focus:ring-1 focus:ring-[#F670C7] focus:outline-none transition-all"
                      placeholder="Type your message..."
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                    />

                    <button
                      type="submit"
                      className={`bg-gradient-to-r from-[#7F56D9] to-[#9D4EDD] text-white p-2.5 rounded-lg ml-2 transition-all ${
                        !newMessage.trim() ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-lg hover:shadow-purple-500/20'
                      }`}
                      disabled={!newMessage.trim()}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" xmlns="http://www.w3.org/2000/svg">
                        <path d="M22 2L11 13M22 2L15 22L11 13M11 13L2 9L22 2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  </div>

                  {showEmojiPicker && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      transition={{ duration: 0.2 }}
                      className="absolute bottom-full left-0 mb-2 rounded-lg bg-[#2A2633] p-2 shadow-xl shadow-black/30 border border-[#3A3643] z-30"
                    >
                      <div className="grid grid-cols-8 gap-1">
                        {["👍", "❤️", "🔥", "😂", "😮", "😢", "👏", "✨", "🎉", "🙌", "🤔", "🙏"].map(emoji => (
                          <button
                            key={emoji}
                            type="button"
                            className="p-1.5 hover:bg-[#3A3643] rounded transition-colors"
                            onClick={() => {
                              setNewMessage(prev => prev + emoji);
                              setShowEmojiPicker(false);
                            }}
                          >
                            {emoji}
                          </button>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </form>
              </>
            ) : (
              <div className="flex items-center justify-center h-full text-center p-8">
                <div>
                  <div className="w-24 h-24 bg-[#2A2633] rounded-full mx-auto mb-6 flex items-center justify-center">
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M22 6.25V11.35C22 12.62 21.58 13.69 20.83 14.43C20.09 15.18 19.02 15.6 17.75 15.6V17.41C17.75 18.09 16.99 18.5 16.43 18.12L15.46 17.48C15.55 17.17 15.59 16.83 15.59 16.47V12.4C15.59 10.36 14.23 9 12.19 9H5.4C5.26 9 5.13 9.01 5 9.02V6.25C5 3.7 6.7 2 9.25 2H17.75C20.3 2 22 3.7 22 6.25Z" stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M15.59 12.4V16.47C15.59 16.83 15.55 17.17 15.46 17.48C15.09 18.95 13.87 19.87 12.19 19.87H9.47L6.45 21.88C6 22.19 5.4 21.86 5.4 21.32V19.87C4.38 19.87 3.53 19.53 2.94 18.94C2.34 18.34 2 17.49 2 16.47V12.4C2 10.5 3.18 9.19 5 9.02C5.13 9.01 5.26 9 5.4 9H12.19C14.23 9 15.59 10.36 15.59 12.4Z" stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Your Messages</h3>
                  <p className="text-gray-400 mb-6">Select a conversation to start messaging</p>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </div>

      {/* Enhanced report message modal */}
      {showReportModal && reportMessage && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[#1F1A27] rounded-xl overflow-hidden w-full max-w-md shadow-2xl"
          >
            <div className="bg-gradient-to-r from-red-500/10 to-red-500/20 px-6 py-4 border-b border-[#3A3643]">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-red-500/20 rounded-full flex items-center justify-center mr-3">
                  <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold">Report Message</h3>
              </div>
            </div>

            <div className="px-6 py-4">
              <div className="mb-4 bg-[#2A2633] p-3 rounded-lg">
                <p className="text-xs text-gray-400 mb-1">Reported message:</p>
                <p className="text-sm text-white">{reportMessage.text}</p>
                <p className="text-xs text-gray-500 mt-1">
                  Sent by: {reportMessage.sender === 'user' ? 'You' : selectedConversation?.user.name}
                </p>
              </div>

              <div className="mb-4 bg-[#2A2633] p-3 rounded-lg">
                <p className="text-xs text-gray-400 mb-2">
                  <svg className="inline-block w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                  Your report helps us maintain a safe community. Please provide as much context as possible to help our moderation team take appropriate action.
                </p>
              </div>

              <p className="text-gray-300 text-sm mb-3 font-medium">
                Please select a reason for reporting this message:
            </p>

            <div className="space-y-2 mb-4">
              {['Inappropriate content', 'Spam', 'Scam/Fraud', 'Harassment', 'Copyright violation', 'Other'].map(reason => (
                  <div
                    key={reason}
                    className={`p-3 rounded-lg cursor-pointer transition-all ${
                      reportReason === reason
                        ? 'bg-[#7F56D9]/20 border border-[#7F56D9]/50'
                        : 'bg-[#2A2633] hover:bg-[#3A3643] border border-transparent'
                    }`}
                    onClick={() => handleReportReasonChange(reason)}
                  >
                    <div className="flex items-center">
                      <div className={`w-4 h-4 rounded-full mr-3 flex items-center justify-center ${
                        reportReason === reason ? 'bg-[#7F56D9]' : 'border border-gray-500'
                      }`}>
                        {reportReason === reason && (
                          <svg className="w-2 h-2 text-white" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <label className="text-sm cursor-pointer">{reason}</label>
                    </div>
                </div>
              ))}
            </div>

              {/* Show contextual help based on selected reason */}
              {reportReason && reportContextQuestions.length > 0 && (
                <div className="mb-4">
                  <p className="text-gray-300 text-sm mb-2 font-medium">
                    Please provide additional details:
                  </p>

                  <div className="bg-[#2A2633]/70 p-3 rounded-lg mb-3">
                    <p className="text-xs text-gray-400">
                      <svg className="inline-block w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                      </svg>
                      Consider including answers to the following questions to help our team understand the issue:
                    </p>
                    <ul className="text-xs text-gray-400 mt-2 pl-5 list-disc space-y-1">
                      {reportContextQuestions.map((question, index) => (
                        <li key={index}>{question}</li>
                      ))}
                    </ul>
                  </div>

              <textarea
                    className="w-full bg-[#2A2633] text-white border-none rounded-lg py-2 px-3 focus:ring-1 focus:ring-[#F670C7] focus:outline-none"
                    placeholder="Please provide details related to your report..."
                    rows={5}
                    value={reportDetails}
                    onChange={(e) => setReportDetails(e.target.value)}
              ></textarea>
                </div>
            )}

              <div className="flex justify-end space-x-3 mt-6">
              <button
                className="px-4 py-2 rounded-lg bg-[#2A2633] hover:bg-[#3A3643] transition-colors"
                  onClick={() => {
                    setShowReportModal(false);
                    setReportReason('');
                    setReportDetails('');
                    setReportMessageId(null);
                    setReportMessage(null);
                    setReportContextQuestions([]);
                  }}
                  disabled={reportSubmitting}
              >
                Cancel
              </button>
              <button
                  className={`px-4 py-2 rounded-lg flex items-center space-x-2 ${
                    !reportReason || (reportReason && reportContextQuestions.length > 0 && !reportDetails.trim()) || reportSubmitting
                      ? 'bg-[#7F56D9]/70 cursor-not-allowed'
                      : 'bg-gradient-to-r from-[#7F56D9] to-[#9D4EDD] hover:shadow-lg hover:shadow-purple-500/20'
                  } text-white transition-all`}
                onClick={submitReport}
                  disabled={!reportReason || (reportReason && reportContextQuestions.length > 0 && !reportDetails.trim()) || reportSubmitting}
                >
                  {reportSubmitting ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Submitting...</span>
                    </>
                  ) : (
                    <span>Submit Report</span>
                  )}
              </button>
            </div>
          </div>
          </motion.div>
        </div>
      )}

      {/* Show reaction picker when a message is selected for reaction */}
      {showReactionPicker && currentReactingMessageId && (
        <div className="fixed inset-0 bg-transparent z-[100]" onClick={() => setShowReactionPicker(false)}>
          <div
            className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-[#2A2633] rounded-xl shadow-lg border border-[#3A3643] p-3"
            onClick={e => e.stopPropagation()}
          >
            <div className="grid grid-cols-6 gap-2">
              {["👍", "❤️", "🔥", "😂", "😮", "😢", "👏", "✨", "🎉", "🙌", "🤔", "🙏"].map(emoji => (
                <button
                  key={emoji}
                  className="p-2 hover:bg-[#3A3643] rounded-lg text-xl"
                  onClick={() => addReaction(currentReactingMessageId, emoji)}
                >
                  {emoji}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default MessagesPage;
